// src/components/festival/MediaGallery.tsx
// DROP 36 — Full Gallery rewrite: Cloudinary + admin upload + lightbox
import React, { useState, useEffect, useCallback } from 'react';
import {
  Image, Video, Upload, Trash2, X, Search,
  Eye, EyeOff, Play, RefreshCw, CheckCircle,
  AlertCircle, ExternalLink, Grid, List
} from 'lucide-react';

const C = {
  bg:'#070D0A', surface:'#0E1812', card:'#121E16', border:'#1C2E22',
  text:'#EDE8DC', muted:'rgba(237,232,220,0.55)', dim:'rgba(237,232,220,0.35)',
  green:'#0F5132', gold:'#C9A84C', red:'#ef4444', blue:'#60a5fa',
};

const inp: React.CSSProperties = {
  width:'100%', background:C.bg, border:`1px solid ${C.border}`,
  borderRadius:7, padding:'9px 12px', color:C.text, fontSize:13,
  outline:'none', boxSizing:'border-box',
};

interface GalleryItem {
  id: string;
  type: 'photo' | 'video';
  category: string;
  title: string;
  description?: string;
  src: string;
  thumbnailSrc?: string;
  community?: string;
  year: number;
  duration?: string;
  isPublic: boolean;
  uploadedAt?: string;
  cloudinaryId?: string;
}

interface Props { currentUser?: any; }

const CATEGORIES = ['All','Ceremony','Cultural','Community','Sports','Diaspora','Royal','Festival 2025'];
const TOKEN = () => localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';

const EMPTY_FORM = {
  type: 'photo' as 'photo'|'video',
  category: 'Ceremony',
  title: '',
  description: '',
  src: '',
  thumbnailSrc: '',
  community: '',
  year: 2026,
  duration: '',
  isPublic: true,
  cloudinaryId: '',
};

export const MediaGallery: React.FC<Props> = ({ currentUser }) => {
  const [items, setItems]           = useState<GalleryItem[]>([]);
  const [loading, setLoading]       = useState(true);
  const [typeFilter, setTypeFilter] = useState<'all'|'photo'|'video'>('all');
  const [catFilter, setCatFilter]   = useState('All');
  const [search, setSearch]         = useState('');
  const [lightbox, setLightbox]     = useState<GalleryItem | null>(null);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState({ ...EMPTY_FORM });
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState<{type:'ok'|'err';text:string}|null>(null);
  const [viewMode, setViewMode]     = useState<'grid'|'masonry'>('grid');
  const [confirmDel, setConfirmDel] = useState<string|null>(null);

  const isAdmin = currentUser && ['PLATFORM_ADMIN','MEDIA_MANAGER','FESTIVAL_COORDINATOR']
    .includes((currentUser.systemRole||currentUser.role||'').toUpperCase());

  const flash = (type:'ok'|'err', text:string) => {
    setMsg({type,text}); setTimeout(()=>setMsg(null),4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ep = isAdmin ? '/api/admin/gallery' : '/api/public/gallery';
      const hdrs: Record<string,string> = isAdmin ? {Authorization:`Bearer ${TOKEN()}`} : {};
      const r = await fetch(ep, {headers:hdrs});
      const d = await r.json();
      if (d.success) setItems(d.items || []);
      else setItems([]);
    } catch { setItems([]); }
    setLoading(false);
  }, [isAdmin]);

  useEffect(()=>{ load(); }, [load]);

  const handleAdd = async () => {
    if (!form.title.trim()) { flash('err','Title required'); return; }
    if (!form.src.trim()) { flash('err','Media URL required (Cloudinary or YouTube)'); return; }
    setSaving(true);
    try {
      const r = await fetch('/api/admin/gallery', {
        method:'POST',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${TOKEN()}`},
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (d.success) {
        flash('ok','Media added to gallery.');
        setShowForm(false);
        setForm({...EMPTY_FORM});
        load();
      } else flash('err', d.error||'Save failed');
    } catch { flash('err','Network error'); }
    setSaving(false);
  };

  const handleDelete = async (id:string) => {
    try {
      const r = await fetch(`/api/admin/gallery/${id}`, {
        method:'DELETE', headers:{Authorization:`Bearer ${TOKEN()}`},
      });
      const d = await r.json();
      if (d.success) { setItems(prev=>prev.filter(x=>x.id!==id)); flash('ok','Item removed.'); }
      else flash('err', d.error||'Delete failed');
    } catch { flash('err','Network error'); }
    setConfirmDel(null);
  };

  const togglePublic = async (item:GalleryItem) => {
    try {
      const r = await fetch(`/api/admin/gallery/${item.id}`, {
        method:'PUT',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${TOKEN()}`},
        body: JSON.stringify({...item, isPublic:!item.isPublic}),
      });
      const d = await r.json();
      if (d.success) setItems(prev=>prev.map(x=>x.id===item.id?{...x,isPublic:!x.isPublic}:x));
    } catch { flash('err','Update failed'); }
  };

  const filtered = items
    .filter(i => typeFilter==='all' || i.type===typeFilter)
    .filter(i => catFilter==='All' || i.category===catFilter)
    .filter(i => !search || (i.title+i.description+i.community+'').toLowerCase().includes(search.toLowerCase()));

  // ── Cloudinary helper ───────────────────────────────────────────────────────
  const cloudSrc = (src:string, w=400) => {
    if (!src) return '';
    if (src.includes('cloudinary.com') && !src.includes('/upload/w_')) {
      return src.replace('/upload/', `/upload/w_${w},c_fill,q_auto/`);
    }
    return src;
  };

  const isYoutube = (src:string) => src.includes('youtube.com') || src.includes('youtu.be');
  const ytId = (src:string) => {
    const m = src.match(/(?:v=|youtu\.be\/)([^&\s]+)/);
    return m ? m[1] : src;
  };

  return (
    <div style={{background:C.bg, minHeight:'100vh', padding:'24px 0'}}>
      <div style={{maxWidth:1200, margin:'0 auto', padding:'0 20px'}}>

        {/* Header */}
        <div style={{background:'linear-gradient(135deg,#060F09 0%,#0A2015 100%)',
          borderRadius:16, padding:'28px 32px', marginBottom:24,
          border:`1px solid rgba(201,168,76,0.2)`}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between',
            flexWrap:'wrap', gap:16}}>
            <div>
              <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:8}}>
                <Image size={18} style={{color:C.gold}} />
                <span style={{fontSize:10, fontWeight:700, letterSpacing:2,
                  textTransform:'uppercase', color:C.gold}}>
                  ERUE Festival Suite · Media Centre
                </span>
              </div>
              <h1 style={{fontFamily:'Cinzel,serif', fontSize:26, fontWeight:700,
                color:C.text, margin:0}}>Festival Gallery</h1>
              <p style={{fontSize:13, color:C.muted, margin:'6px 0 0'}}>
                Photos, videos and multimedia from the ERUE Festival of Uzairue Kingdom
              </p>
            </div>
            <div style={{display:'flex', gap:10, alignItems:'center'}}>
              <button onClick={load} style={{padding:'8px 12px', background:'none',
                border:`1px solid ${C.border}`, borderRadius:8, color:C.muted,
                cursor:'pointer', display:'flex', alignItems:'center', gap:4}}>
                <RefreshCw size={13} />
              </button>
              {isAdmin && (
                <button onClick={()=>setShowForm(true)}
                  style={{display:'flex', alignItems:'center', gap:7, padding:'10px 20px',
                    background:C.green, border:`1px solid rgba(201,168,76,0.4)`,
                    borderRadius:10, color:C.gold, fontSize:13, fontWeight:700, cursor:'pointer'}}>
                  <Upload size={15} /> Add Media
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Flash */}
        {msg && (
          <div style={{padding:'10px 16px', borderRadius:8, marginBottom:16,
            background:msg.type==='ok'?'rgba(34,197,94,0.1)':'rgba(239,68,68,0.1)',
            border:`1px solid ${msg.type==='ok'?'rgba(34,197,94,0.3)':'rgba(239,68,68,0.3)'}`,
            color:msg.type==='ok'?'#22c55e':C.red, fontSize:13,
            display:'flex', alignItems:'center', gap:8}}>
            {msg.type==='ok'?<CheckCircle size={14}/>:<AlertCircle size={14}/>}
            {msg.text}
          </div>
        )}

        {/* Stats */}
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',
          gap:12, marginBottom:24}}>
          {[
            {label:'Total Items', value:items.length, icon:'🗂️'},
            {label:'Photos', value:items.filter(i=>i.type==='photo').length, icon:'📷'},
            {label:'Videos', value:items.filter(i=>i.type==='video').length, icon:'🎬'},
            {label:'Public', value:items.filter(i=>i.isPublic).length, icon:'🌐'},
          ].map((s,i)=>(
            <div key={i} style={{background:C.card, border:`1px solid ${C.border}`,
              borderRadius:10, padding:'12px 16px', textAlign:'center'}}>
              <div style={{fontSize:18, marginBottom:4}}>{s.icon}</div>
              <div style={{fontSize:20, fontWeight:700, color:C.gold}}>{s.value}</div>
              <div style={{fontSize:10, color:C.muted, textTransform:'uppercase',
                letterSpacing:1}}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{display:'flex', gap:10, flexWrap:'wrap', marginBottom:16, alignItems:'center'}}>
          {/* Type filter */}
          <div style={{display:'flex', gap:4}}>
            {(['all','photo','video'] as const).map(t=>(
              <button key={t} onClick={()=>setTypeFilter(t)}
                style={{padding:'7px 14px', background:typeFilter===t?C.green:'none',
                  border:`1px solid ${typeFilter===t?C.gold:C.border}`, borderRadius:7,
                  color:typeFilter===t?C.gold:C.muted, fontSize:12, cursor:'pointer',
                  textTransform:'capitalize', display:'flex', alignItems:'center', gap:4}}>
                {t==='photo'&&<Image size={11}/>}
                {t==='video'&&<Video size={11}/>}
                {t==='all'?'All Media':t==='photo'?'Photos':'Videos'}
              </button>
            ))}
          </div>
          {/* Category */}
          <select value={catFilter} onChange={e=>setCatFilter(e.target.value)}
            style={{...inp, width:180, cursor:'pointer', padding:'7px 12px'}}>
            {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          {/* Search */}
          <div style={{flex:1, minWidth:180, position:'relative'}}>
            <Search size={13} style={{position:'absolute', left:10, top:'50%',
              transform:'translateY(-50%)', color:C.muted, pointerEvents:'none'}}/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search gallery..." style={{...inp, paddingLeft:32, padding:'7px 12px 7px 32px'}}/>
          </div>
          {/* View toggle */}
          <div style={{display:'flex', gap:4}}>
            {(['grid','masonry'] as const).map(m=>(
              <button key={m} onClick={()=>setViewMode(m)}
                style={{padding:'7px 12px', background:viewMode===m?C.green:'none',
                  border:`1px solid ${viewMode===m?C.gold:C.border}`, borderRadius:7,
                  color:viewMode===m?C.gold:C.muted, fontSize:11, cursor:'pointer',
                  textTransform:'capitalize'}}>
                {m==='grid'?<Grid size={13}/>:<List size={13}/>}
              </button>
            ))}
          </div>
        </div>

        <div style={{fontSize:12, color:C.dim, marginBottom:16}}>
          Showing {filtered.length} of {items.length} items
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div style={{textAlign:'center', padding:80}}>
            <div style={{width:32, height:32, border:`3px solid ${C.border}`,
              borderTopColor:C.gold, borderRadius:'50%', margin:'0 auto 16px',
              animation:'spin 1s linear infinite'}}/>
            <p style={{color:C.muted, fontSize:13}}>Loading gallery...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{textAlign:'center', padding:80}}>
            <Image size={48} style={{margin:'0 auto 16px', opacity:0.3, display:'block', color:C.muted}}/>
            <p style={{fontSize:15, color:C.muted, marginBottom:8}}>No media found</p>
            <p style={{fontSize:13, color:C.dim}}>
              {isAdmin ? 'Click "Add Media" to upload photos and videos.' : 'Gallery content coming soon.'}
            </p>
            {isAdmin && (
              <button onClick={()=>setShowForm(true)}
                style={{marginTop:20, padding:'10px 24px', background:C.green,
                  border:`1px solid rgba(201,168,76,0.4)`, borderRadius:10,
                  color:C.gold, fontSize:13, fontWeight:700, cursor:'pointer'}}>
                + Add First Media Item
              </button>
            )}
          </div>
        ) : (
          <div style={{
            display:'grid',
            gridTemplateColumns: viewMode==='grid'
              ? 'repeat(auto-fill,minmax(280px,1fr))'
              : 'repeat(auto-fill,minmax(220px,1fr))',
            gap:16,
          }}>
            {filtered.map(item=>(
              <div key={item.id} style={{background:C.card, border:`1px solid ${C.border}`,
                borderRadius:12, overflow:'hidden', position:'relative',
                cursor:'pointer', transition:'border-color 0.2s'}}
                onClick={()=>setLightbox(item)}>

                {/* Visibility badge */}
                {isAdmin && (
                  <div style={{position:'absolute', top:8, left:8, zIndex:3,
                    padding:'2px 8px', borderRadius:20, fontSize:9, fontWeight:700,
                    background:item.isPublic?'rgba(34,197,94,0.8)':'rgba(239,68,68,0.8)',
                    color:'#fff'}}>
                    {item.isPublic?'PUBLIC':'PRIVATE'}
                  </div>
                )}

                {/* Type badge */}
                <div style={{position:'absolute', top:8, right:8, zIndex:3,
                  padding:'2px 8px', borderRadius:20, fontSize:9, fontWeight:700,
                  background:'rgba(0,0,0,0.6)', color:C.gold, border:`1px solid rgba(201,168,76,0.3)`}}>
                  {item.type==='video'?'🎬 VIDEO':'📷 PHOTO'}
                </div>

                {/* Thumbnail */}
                <div style={{height:180, background:`linear-gradient(135deg,#0A1F14,#0F5132)`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  overflow:'hidden', position:'relative'}}>
                  {item.type==='video' && isYoutube(item.src) ? (
                    <img
                      src={`https://img.youtube.com/vi/${ytId(item.src)}/hqdefault.jpg`}
                      alt={item.title}
                      style={{width:'100%', height:'100%', objectFit:'cover'}}
                      onError={e=>{(e.target as HTMLImageElement).style.display='none';}}
                    />
                  ) : item.src && item.src.includes('cloudinary') ? (
                    <img
                      src={cloudSrc(item.thumbnailSrc||item.src, 400)}
                      alt={item.title}
                      style={{width:'100%', height:'100%', objectFit:'cover'}}
                      onError={e=>{(e.target as HTMLImageElement).style.display='none';}}
                    />
                  ) : (
                    <div style={{fontSize:48, textAlign:'center', opacity:0.5}}>
                      {item.type==='video'?'🎬':'📷'}
                    </div>
                  )}
                  {item.type==='video' && (
                    <div style={{position:'absolute', inset:0, display:'flex',
                      alignItems:'center', justifyContent:'center',
                      background:'rgba(0,0,0,0.3)'}}>
                      <div style={{width:44, height:44, borderRadius:'50%',
                        background:'rgba(201,168,76,0.9)', display:'flex',
                        alignItems:'center', justifyContent:'center'}}>
                        <Play size={18} style={{color:'#000', marginLeft:2}} fill="#000"/>
                      </div>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div style={{padding:'12px 14px'}}>
                  <div style={{fontSize:10, color:C.gold, fontWeight:700,
                    letterSpacing:1, textTransform:'uppercase', marginBottom:3}}>
                    {item.category} · {item.year}
                  </div>
                  <div style={{fontSize:13, fontWeight:600, color:C.text,
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                    marginBottom:4}}>{item.title}</div>
                  {item.community && (
                    <div style={{fontSize:11, color:C.dim}}>📍 {item.community}</div>
                  )}
                  {item.duration && (
                    <div style={{fontSize:11, color:C.muted, marginTop:2}}>⏱ {item.duration}</div>
                  )}

                  {/* Admin controls */}
                  {isAdmin && (
                    <div style={{display:'flex', gap:6, marginTop:10,
                      paddingTop:10, borderTop:`1px solid ${C.border}`}}
                      onClick={e=>e.stopPropagation()}>
                      <button onClick={()=>togglePublic(item)}
                        style={{padding:'4px 10px', background:'none',
                          border:`1px solid ${C.border}`, borderRadius:5,
                          color:item.isPublic?'#22c55e':C.muted,
                          fontSize:10, cursor:'pointer'}}>
                        {item.isPublic?<Eye size={11}/>:<EyeOff size={11}/>}
                      </button>
                      <a href={item.src} target="_blank" rel="noreferrer"
                        style={{padding:'4px 10px', background:'none',
                          border:`1px solid ${C.border}`, borderRadius:5,
                          color:C.blue, fontSize:10, textDecoration:'none',
                          display:'flex', alignItems:'center', gap:3}}>
                        <ExternalLink size={11}/>
                      </a>
                      <button onClick={()=>setConfirmDel(item.id)}
                        style={{padding:'4px 10px', background:'none',
                          border:`1px solid rgba(239,68,68,0.3)`, borderRadius:5,
                          color:C.red, fontSize:10, cursor:'pointer', marginLeft:'auto'}}>
                        <Trash2 size={11}/>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Lightbox ──────────────────────────────────────────────────────────── */}
      {lightbox && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.92)',
          zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center',
          padding:20}} onClick={()=>setLightbox(null)}>
          <div style={{maxWidth:900, width:'100%', background:C.card,
            borderRadius:16, overflow:'hidden', border:`1px solid ${C.border}`}}
            onClick={e=>e.stopPropagation()}>
            <div style={{position:'relative'}}>
              <button onClick={()=>setLightbox(null)}
                style={{position:'absolute', top:12, right:12, zIndex:10,
                  background:'rgba(0,0,0,0.7)', border:'none', borderRadius:'50%',
                  width:32, height:32, color:'#fff', fontSize:18, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center'}}>
                <X size={16}/>
              </button>

              {lightbox.type==='video' && isYoutube(lightbox.src) ? (
                <div style={{position:'relative', paddingBottom:'56.25%', height:0}}>
                  <iframe
                    src={`https://www.youtube.com/embed/${ytId(lightbox.src)}?autoplay=1`}
                    style={{position:'absolute', top:0, left:0, width:'100%', height:'100%',
                      border:'none'}}
                    allowFullScreen
                    allow="autoplay; encrypted-media"
                    title={lightbox.title}
                  />
                </div>
              ) : lightbox.src && lightbox.src.includes('cloudinary') ? (
                <img src={cloudSrc(lightbox.src, 900)} alt={lightbox.title}
                  style={{width:'100%', maxHeight:500, objectFit:'contain',
                    background:'#000'}}/>
              ) : (
                <div style={{height:300, display:'flex', alignItems:'center',
                  justifyContent:'center', background:'#000', fontSize:64}}>
                  {lightbox.type==='video'?'🎬':'📷'}
                </div>
              )}
            </div>
            <div style={{padding:'16px 20px'}}>
              <div style={{fontSize:10, color:C.gold, fontWeight:700,
                textTransform:'uppercase', letterSpacing:1, marginBottom:4}}>
                {lightbox.category} · {lightbox.year}
                {lightbox.community && ` · ${lightbox.community}`}
              </div>
              <h3 style={{fontFamily:'Cinzel,serif', fontSize:18, color:C.text,
                margin:'0 0 6px'}}>{lightbox.title}</h3>
              {lightbox.description && (
                <p style={{fontSize:13, color:C.muted, lineHeight:1.6, margin:0}}>
                  {lightbox.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Add Media Form Modal ───────────────────────────────────────────────── */}
      {showForm && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.75)',
          zIndex:1000, display:'flex', alignItems:'flex-start',
          justifyContent:'center', padding:20, overflowY:'auto'}}>
          <div style={{background:'#0E1812', border:`1px solid ${C.border}`,
            borderRadius:16, padding:28, width:'100%', maxWidth:600, margin:'20px auto'}}>
            <div style={{display:'flex', justifyContent:'space-between',
              alignItems:'center', marginBottom:22}}>
              <h2 style={{fontFamily:'Cinzel,serif', fontSize:18, color:C.gold, margin:0}}>
                + Add Media to Gallery
              </h2>
              <button onClick={()=>setShowForm(false)}
                style={{background:'none', border:'none', color:C.muted,
                  fontSize:22, cursor:'pointer', lineHeight:1}}>×</button>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
              {/* Type */}
              <div>
                <label style={{display:'block', fontSize:10, fontWeight:700,
                  letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4}}>
                  Media Type *
                </label>
                <select value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value as any}))}
                  style={{...inp, cursor:'pointer'}}>
                  <option value="photo">📷 Photo</option>
                  <option value="video">🎬 Video</option>
                </select>
              </div>
              {/* Category */}
              <div>
                <label style={{display:'block', fontSize:10, fontWeight:700,
                  letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4}}>
                  Category *
                </label>
                <select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}
                  style={{...inp, cursor:'pointer'}}>
                  {CATEGORIES.filter(c=>c!=='All').map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Title */}
              <div style={{gridColumn:'1 / -1'}}>
                <label style={{display:'block', fontSize:10, fontWeight:700,
                  letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4}}>
                  Title *
                </label>
                <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))}
                  placeholder="e.g. Grand Opening Ceremony 2026" style={inp}/>
              </div>

              {/* Media URL */}
              <div style={{gridColumn:'1 / -1'}}>
                <label style={{display:'block', fontSize:10, fontWeight:700,
                  letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4}}>
                  {form.type==='video' ? 'YouTube URL or Video URL *' : 'Cloudinary Image URL *'}
                </label>
                <input value={form.src}
                  onChange={e=>setForm(p=>({...p,src:e.target.value}))}
                  placeholder={form.type==='video'
                    ? 'https://www.youtube.com/watch?v=...'
                    : 'https://res.cloudinary.com/uuovmrek/image/upload/...'}
                  style={inp}/>
                {form.type==='photo' && (
                  <p style={{fontSize:10, color:C.dim, marginTop:4}}>
                    Upload to Cloudinary first, then paste the URL here.
                    Cloud Name: <strong style={{color:C.gold}}>uuovmrek</strong>
                  </p>
                )}
              </div>

              {/* Thumbnail (optional for video) */}
              {form.type==='photo' && (
                <div style={{gridColumn:'1 / -1'}}>
                  <label style={{display:'block', fontSize:10, fontWeight:700,
                    letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4}}>
                    Thumbnail URL (optional)
                  </label>
                  <input value={form.thumbnailSrc}
                    onChange={e=>setForm(p=>({...p,thumbnailSrc:e.target.value}))}
                    placeholder="Smaller version for grid (auto-generated from Cloudinary)"
                    style={inp}/>
                </div>
              )}

              {/* Description */}
              <div style={{gridColumn:'1 / -1'}}>
                <label style={{display:'block', fontSize:10, fontWeight:700,
                  letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4}}>
                  Description
                </label>
                <textarea value={form.description} rows={2}
                  onChange={e=>setForm(p=>({...p,description:e.target.value}))}
                  placeholder="Brief description of this media item..."
                  style={{...inp, resize:'vertical'}}/>
              </div>

              {/* Community + Year + Duration */}
              <div>
                <label style={{display:'block', fontSize:10, fontWeight:700,
                  letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4}}>
                  Community (optional)
                </label>
                <input value={form.community}
                  onChange={e=>setForm(p=>({...p,community:e.target.value}))}
                  placeholder="e.g. Jattu-Uzairue" style={inp}/>
              </div>
              <div>
                <label style={{display:'block', fontSize:10, fontWeight:700,
                  letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4}}>
                  Year
                </label>
                <input type="number" value={form.year}
                  onChange={e=>setForm(p=>({...p,year:parseInt(e.target.value)||2026}))}
                  style={inp}/>
              </div>
              {form.type==='video' && (
                <div>
                  <label style={{display:'block', fontSize:10, fontWeight:700,
                    letterSpacing:1, textTransform:'uppercase', color:C.muted, marginBottom:4}}>
                    Duration (e.g. 4:32)
                  </label>
                  <input value={form.duration}
                    onChange={e=>setForm(p=>({...p,duration:e.target.value}))}
                    placeholder="4:32" style={inp}/>
                </div>
              )}

              {/* Public toggle */}
              <div style={{gridColumn:'1 / -1', display:'flex', alignItems:'center', gap:12}}>
                <button onClick={()=>setForm(p=>({...p,isPublic:!p.isPublic}))}
                  style={{width:44, height:24, borderRadius:12, border:'none',
                    background:form.isPublic?'#22c55e':C.border, cursor:'pointer',
                    position:'relative', transition:'background 0.2s'}}>
                  <div style={{position:'absolute', top:3,
                    left:form.isPublic?22:3, width:18, height:18, borderRadius:'50%',
                    background:'#fff', transition:'left 0.2s'}}/>
                </button>
                <span style={{fontSize:13, color:C.muted}}>
                  {form.isPublic?'🌐 Visible on public gallery':'🔒 Private — admin only'}
                </span>
              </div>
            </div>

            <div style={{display:'flex', gap:10, marginTop:22}}>
              <button onClick={handleAdd} disabled={saving}
                style={{flex:1, padding:'11px 20px', background:C.green,
                  border:`1px solid rgba(201,168,76,0.4)`, borderRadius:9,
                  color:C.gold, fontSize:13, fontWeight:700,
                  cursor:saving?'wait':'pointer', opacity:saving?0.6:1}}>
                {saving?'Uploading...':'+ Add to Gallery'}
              </button>
              <button onClick={()=>setShowForm(false)}
                style={{padding:'11px 20px', background:'transparent',
                  border:`1px solid ${C.border}`, borderRadius:9,
                  color:C.muted, fontSize:13, cursor:'pointer'}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ─────────────────────────────────────────────────────── */}
      {confirmDel && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.75)',
          zIndex:1001, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div style={{background:'#0E1812', border:`1px solid rgba(239,68,68,0.3)`,
            borderRadius:14, padding:28, maxWidth:360, width:'90%', textAlign:'center'}}>
            <Trash2 size={32} style={{color:C.red, margin:'0 auto 14px', display:'block'}}/>
            <h3 style={{fontFamily:'Cinzel,serif', fontSize:16, color:C.text, marginBottom:8}}>
              Remove Media Item?
            </h3>
            <p style={{fontSize:13, color:C.muted, marginBottom:20}}>
              This will remove the item from the gallery. The Cloudinary asset is not deleted.
            </p>
            <div style={{display:'flex', gap:10, justifyContent:'center'}}>
              <button onClick={()=>handleDelete(confirmDel)}
                style={{padding:'9px 24px', background:'rgba(239,68,68,0.15)',
                  border:`1px solid rgba(239,68,68,0.4)`, borderRadius:8,
                  color:C.red, fontSize:13, fontWeight:700, cursor:'pointer'}}>
                Yes, Remove
              </button>
              <button onClick={()=>setConfirmDel(null)}
                style={{padding:'9px 24px', background:'none',
                  border:`1px solid ${C.border}`, borderRadius:8,
                  color:C.muted, fontSize:13, cursor:'pointer'}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default MediaGallery;
